import * as React from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthProvider';
import { EmptyState } from '@/components/ui/empty-state';

export interface SettingsSectionProps {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** Heading + description + body of one settings sub-page. */
export function SettingsSection({ title, description, actions, className, children }: SettingsSectionProps) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} className={cn('space-y-5', className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h2 id={id} className="text-lg font-semibold tracking-tight">
            {title}
          </h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Small callout shown to non-admins on pages they can read but not change. */
export function AdminNote({ what = 'these settings' }: { what?: string }) {
  return (
    <p
      role="note"
      className="flex items-start gap-2 rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
    >
      <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>Read-only: only administrators can change {what}.</span>
    </p>
  );
}

/** Renders the children for admins only; others see an "Administrators only" state (no data is fetched). */
export function AdminOnly({ what, children }: { what: string; children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (isAdmin) return <>{children}</>;
  return (
    <EmptyState
      icon={Lock}
      title="Administrators only"
      description={`Only administrators can manage ${what}. Ask an admin if you need a change.`}
    />
  );
}

/** Card-like group used inside settings pages. */
export function SettingsCard({
  title,
  description,
  children,
  className,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border bg-card p-5 text-card-foreground shadow-sm', className)}>
      {title ? <h3 className="text-sm font-semibold">{title}</h3> : null}
      {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      <div className={cn(title || description ? 'mt-4' : undefined)}>{children}</div>
    </div>
  );
}
